import type { Extensions } from "@tiptap/core";
import type * as Y from "yjs";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import { Placeholder } from "@tiptap/extension-placeholder";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontFamily } from "@tiptap/extension-font-family";
import { Highlight } from "@tiptap/extension-highlight";
import { Underline } from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import {
    ImageUpload,
    Mermaid,
    BridgeTable,
    TableCellExtended,
    FontSize,
    SVGPassthrough,
    ExcalidrawExtension
} from "./extensions";
import { AgentSuggestion } from "@/components/aiBridge/AgentSuggestionExtension";
import { AgentSuggestionBlock } from "@/components/aiBridge/AgentSuggestionBlock";
import { AgentPendingReplace } from "@/components/aiBridge/AgentPendingReplaceMark";
import Collaboration from "@tiptap/extension-collaboration";

export interface EditorCollabBinding {
    fragment: Y.XmlFragment;
}

export interface EditorExtensionOptions {
    mode: "mounted" | "headless";
    /** Auth token for ImageUpload; headless editors never upload, pass "". */
    token?: string;
    collab?: EditorCollabBinding;
}

/**
 * Single source of truth for the TipTap extension set. Mounted (visible)
 * and headless (agent) editors MUST produce identical PM schemas — Yjs
 * peers with divergent schemas silently drop or corrupt nodes. Only
 * schema-free, view-only extensions (Placeholder)
 * may differ by mode.
 */
export function buildEditorExtensions({ mode, token, collab }: EditorExtensionOptions): Extensions {
    return [
        StarterKit.configure({
            heading: {
                levels: [1, 2, 3, 4, 5, 6]
            },
            codeBlock: {
                HTMLAttributes: {
                    class: "tiptap-code-block"
                }
            },
            // Yjs provides collaborative undo — the built-in history
            // plugin conflicts with it and must be off in collab mode.
            ...(collab ? { history: false as const } : {})
        }),
        Markdown.configure({
            html: true, // CRITICAL: Allow HTML in markdown (for compatibility with existing docs)
            tightLists: true,
            bulletListMarker: "-",
            linkify: true,
            breaks: true,
            transformPastedText: true,
            transformCopiedText: true
        }),
        BridgeTable.configure({
            resizable: false,
            HTMLAttributes: {
                class: "tiptap-table"
            }
        }),
        TableRow,
        TableHeader,
        TableCellExtended,
        TextStyle, // Required for Color, FontFamily, and FontSize
        Color,
        FontFamily.configure({
            types: ["textStyle"]
        }),
        FontSize, // Font size support
        Underline, // Underline support
        Highlight.configure({
            multicolor: true
        }),
        TextAlign.configure({
            types: ["heading", "paragraph", "mermaid"],
            alignments: ["left", "center", "right", "justify"],
            defaultAlignment: "left"
        }),
        ImageUpload(token ?? ""), // Use custom image upload extension
        Mermaid, // Mermaid diagram support
        ExcalidrawExtension, // Excalidraw diagram support
        SVGPassthrough, // SVG pass-through (no processing, just render)
        AgentSuggestion, // inline mark — strike-through original + green chip for single-line text edits
        AgentPendingReplace, // strike-through mark on anchor block when paired with a block-level suggestion
        AgentSuggestionBlock, // block-level wrapper for multi-block proposed content (add paragraph, list, etc.)
        ...(mode === "mounted"
            ? [
                  Placeholder.configure({
                      placeholder: ({ node }: { node: { type: { name: string }; attrs: { level?: number } } }) => {
                          if (node.type.name === "heading") {
                              return `Heading ${node.attrs.level}...`;
                          }
                          return "Start typing...";
                      }
                  })
              ]
            : []),
        ...(collab ? [Collaboration.configure({ fragment: collab.fragment })] : [])
    ];
}
